const checkoutStageConstants = require('./constants');
const { changeViewToNormal } = require('../checkoutViewManager').methods;
const { checkoutEventBus } = require('../events');

const stagesArray = Object.values(checkoutStageConstants);

const stageParam = new URLSearchParams(window.location.search).get('stage');
const initialStage =
  stageParam !== null && isValidStage(stageParam)
    ? stageParam
    : checkoutStageConstants.SHIPPING_METHOD;

let currentStage = initialStage;
let placeOrderReached = false;

/**
 * Gets the current checkout stage.
 * @returns {string} The current stage.
 */
function getStage() {
  return currentStage;
}

/**
 * Checks if the given stage is part of the payment group.
 * @param {string} [stage=currentStage] - The stage to check.
 * @returns {boolean} True if stage is in payment group.
 */
function isStageFromPaymentGroup(stage = currentStage) {
  const paymentStagesArray = [
    checkoutStageConstants.PAYMENT_METHOD,
    checkoutStageConstants.PAYMENT_DETAILS,
    checkoutStageConstants.SECOND_PAYMENT_METHOD,
    checkoutStageConstants.SECOND_PAYMENT_DETAILS,
    checkoutStageConstants.BILLING_ADDRESS,
    checkoutStageConstants.PLACE_ORDER,
  ];
  return isValidStage(stage) && paymentStagesArray.includes(stage);
}

/**
 * Checks if the given stage is part of the shipping group.
 * @param {string} [stage=currentStage] - The stage to check.
 * @returns {boolean} True if stage is in shipping group.
 */
function isStageFromShippingGroup(stage = currentStage) {
  const shippingStagesArray = [
    checkoutStageConstants.SHIPPING_METHOD,
    checkoutStageConstants.SHIPPING_ADDRESS,
    checkoutStageConstants.SHIPPING_SUMMARY,
  ];
  return isValidStage(stage) && shippingStagesArray.includes(stage);
}

/**
 * Validates if the given stage exists in the stages array.
 * @param {string} stage - The stage to validate.
 * @returns {boolean} True if valid stage.
 */
function isValidStage(stage) {
  return stagesArray.includes(stage);
}

/**
 * Handles browser history changes for checkout stages.
 * @param {string} newStage - The new stage to set.
 * @param {boolean} isInitialState - Whether this is the initial state.
 */
function handleHistoryChange(newStage, isInitialState) {
  const urlParams = new URLSearchParams(window.location.search);

  if (isInitialState) {
    const shipping = require('../shipping');
    const startingStage = shipping.methods.isOnlineShippingMethodSelected()
      ? checkoutStageConstants.PAYMENT_METHOD
      : checkoutStageConstants.SHIPPING_METHOD;
    urlParams.set('stage', startingStage);
    window.history.replaceState(
      { stage: startingStage },
      document.title,
      `?${urlParams}`
    );

    // Push checkoutStageConstants from starting stage next stage until you reach the current one
    const stageIds = Object.values(checkoutStageConstants);
    const startingStageIndex = stageIds.indexOf(startingStage);
    for (let i = startingStageIndex + 1; i <= stageIds.indexOf(newStage); i++) {
      urlParams.set(
        'stage',
        checkoutStageConstants[Object.keys(checkoutStageConstants)[i]]
      );
      window.history.pushState(
        {
          stage: checkoutStageConstants[Object.keys(checkoutStageConstants)[i]],
        },
        document.title,
        `?${urlParams}`
      );
    }
  } else if (newStage !== currentStage) {
    // Push new state into history
    urlParams.set('stage', newStage);
    window.history.pushState(
      { stage: newStage },
      document.title,
      `?${urlParams}`
    );
  } else if (!stageParam) {
    // newStage is same as current, but with no stageParam, then just add the stage into the url
    urlParams.set('stage', newStage);
    window.history.replaceState(
      { stage: newStage },
      document.title,
      `?${urlParams}`
    );
  }
}

/**
 * Sets the global variable if place order stage has been reached.
 * @param {string} newStage - The new stage to check.
 */
function setPlaceOrderReached(newStage) {
  if (!placeOrderReached) {
    placeOrderReached = newStage === checkoutStageConstants.PLACE_ORDER;
  } else if (newStage === checkoutStageConstants.SHIPPING_METHOD) {
    placeOrderReached = false;
  }
}

/**
 * Checks if place order has been reached and flow is valid.
 * @param {string} newStage - The new stage to check.
 * @returns {boolean} True if should go to place order.
 */
function checkIfGoToPlaceOrder(newStage) {
  return (
    placeOrderReached &&
    (currentStage === checkoutStageConstants.SHIPPING_SUMMARY ||
      currentStage === checkoutStageConstants.BILLING_ADDRESS) &&
    isStageFromPaymentGroup(newStage)
  );
}

/**
 * Returns the next stage, or place order if conditions are met.
 * @param {string} newStage - The new stage to check.
 * @returns {string} The next stage.
 */
function getNextStage(newStage) {
  const goToPlaceOrder = checkIfGoToPlaceOrder(newStage);
  return goToPlaceOrder ? checkoutStageConstants.PLACE_ORDER : newStage;
}

/**
 * Sets the current stage and updates history and UI.
 * @param {string} newStage - The new stage to set.
 * @param {boolean} [changeHistory=true] - Whether to change browser history.
 * @param {boolean} [isInitialState=false] - Whether this is the initial state.
 */
function setStage(newStage, changeHistory = true, isInitialState = false) {
  if (!isValidStage(newStage)) {
    return;
  }

  setPlaceOrderReached(newStage);
  const nextStage = getNextStage(newStage);

  // If the navigation is done via browser navigation, skip this logic
  if (changeHistory) handleHistoryChange(nextStage, isInitialState);

  currentStage = nextStage;
  changeViewToNormal(); // first part of each stage is always normal view
  checkoutEventBus.publish(checkoutEventBus.EVENTS.checkoutStageChanged, {
    currentStage,
  });
}

/**
 * Advances to the next checkout stage.
 */
function setNextStage() {
  const newIndex = stagesArray.indexOf(getStage()) + 1;
  const nextStage =
    stagesArray[
      newIndex < stagesArray.length ? newIndex : stagesArray.length - 1
    ];
  setStage(nextStage);
}

module.exports = {
  methods: {
    getStage,
    isStageFromPaymentGroup,
    isStageFromShippingGroup,
    setNextStage,
    setStage,
  },
};
